import clsx from 'clsx'
import { useEffect, useRef } from 'react'

import { Icon } from '@fv/client-components'
import {
  applyTaxes,
  currencyToString,
  getAmount,
  isManagedTax,
  useCurrencyOptions,
} from '@fv/client-core'
import type { Quote } from '@fv/client-types'
import type { Currency } from '@fv/models'
import type { EquipmentType } from '@fv/models/equipment'

import { CarrierDateSelector } from '@/components/CarrierDateSelector'
import { CarrierTooltip } from '@/components/CarrierTooltip'
import { CheckboxInput } from '@/components/inputs/CheckboxInput'
import { SelectInput } from '@/components/inputs/SelectInput'
import { TextInput } from '@/components/inputs/TextInput'
import { useAppSettings } from '@/hooks/settings'
import { useExchangeRatesForLoad } from '@/hooks/useExchangeRate'

import { BaseRateField } from './BaseRateField'
import { ChargeBreakdownControls } from './ChargeBreakdownControls'
import {
  useQuotesFormActions,
  useQuotesFormHelpers,
  useQuotesFormState,
} from './QuotesFormProvider'
import type { Model } from './types'

type QuoteProps = Pick<Quote, 'method' | 'source'>
type Props = {
  value: Model
  disabled?: boolean
  loading?: boolean
  quote?: QuoteProps
}
export const QuoteFormEntry = ({ value, loading, disabled, quote }: Props) => {
  const amountRef = useRef<HTMLInputElement>(null)
  const numRef = useRef<HTMLInputElement>(null)
  const settings = useAppSettings().data
  const quoteType = quoteTypeLabel(quote)
  const { equipmentOptions, selected, contractedRatesLocked, opportunity } =
    useQuotesFormState()
  const { updateModel, toggleSelected } = useQuotesFormActions()
  const { switchFromCharges } = useQuotesFormHelpers()
  const { method, source } = quote ?? {}
  const isApiRate = method === 'api'
  const isCarrierRate = !quote || (method === 'manual' && source === 'carrier')
  const isContractedRate = method === 'upload'
  const isShipperRate = method === 'manual' && source === 'shipper'
  const isSelected = selected.includes(value.quoteId)
  const isNonUsd = value.currency !== 'usd'
  const exchangeRates = useExchangeRatesForLoad(opportunity, isNonUsd)
  const currentRate = exchangeRates[value.currency]
  const inputName = (name: string) => `${value.quoteId ?? ''}-${name}`

  useEffect(() => {
    amountRef.current?.setCustomValidity(value.errors?.amount ?? '')
    numRef.current?.setCustomValidity(value.errors?.quoteNum ?? '')
  }, [value.errors])

  const handleToggleSelected = (toggle: boolean) => {
    if (!toggle) {
      updateModel({
        ...value,
        quoteNum: '',
        amount: 0,
      })
    }
    toggleSelected(value.quoteId, toggle)
  }
  const currencyOptions = useCurrencyOptions(opportunity?.locations)

  const handleCurrencyChange = (newCurrency: Currency) => {
    handleToggleSelected(true)
    handleUpdate({ ...value, currency: newCurrency })
  }

  const handleUpdate = (update: Model) => {
    const updated = applyTaxes(update, opportunity?.locations)
    const hasTaxCharges = updated.charges?.some(c =>
      isManagedTax(c.name, updated.currency, opportunity?.locations),
    )
    const needsSwitched =
      value.charges?.length > 1 && (updated.charges?.length ?? 0) <= 1

    updateModel(updated)
    if (needsSwitched && !hasTaxCharges) switchFromCharges(updated)
  }
  return (
    <div>
      {isContractedRate && !contractedRatesLocked && (
        <div className="box mt-4 bg-white">
          <div className="icon-message">
            <Icon
              className="text-fv-orange"
              icon="hand-paper"
              transform="down-1"
            />
            <p className="mb-0">
              This is a contracted rate. Your company has negotiated a
              pre-determined rate for this lane. Unlocking and modifying this
              rate will override your contracted rate. The shipper will then be
              shown the new rate. Select 'Update bid' to proceed or 'X' to
              cancel and keep the contracted rate.
            </p>
          </div>
        </div>
      )}

      <div
        className={clsx(
          'b850:flex-wrap b1500:flex-wrap b1000:flex-nowrap my-4 flex items-center gap-y-3 gap-x-2',
        )}
      >
        <div className="flex items-center gap-x-2 gap-y-3">
          <CheckboxInput
            checked={isSelected}
            className={clsx('checkbox', {
              disabled: loading || !isCarrierRate,
            })}
            disabled={loading || !isCarrierRate}
            label={quoteType + ' quote'}
            name={inputName(`selected`)}
            onChange={e => handleToggleSelected(e.target.checked)}
            required={!quote}
          />

          {isShipperRate && (
            <CarrierTooltip label="This quote was entered by the shipper">
              <Icon icon="user-circle" />
            </CarrierTooltip>
          )}

          {isContractedRate && (
            <CarrierTooltip label="This is a contracted rate">
              <Icon icon="money-check-alt" transform="down-.5" />
            </CarrierTooltip>
          )}

          {isApiRate && (
            <CarrierTooltip label="Automatically generated via API">
              <Icon icon="server" />
            </CarrierTooltip>
          )}
        </div>

        <div className="h-0 flex-1 border border-dashed border-b-[#45abd3]" />

        {equipmentOptions.length > 1 && (
          <SelectInput
            className="w-auto"
            value={value.equipmentType}
            disabled={disabled}
            name={inputName(`equip-type`)}
            onChange={e => {
              handleUpdate({
                ...value,
                equipmentType: e.target.value as EquipmentType,
              })
            }}
            options={equipmentOptions}
          />
        )}

        {opportunity.workflow === 'truckload' && (
          <CarrierDateSelector
            value={value.deliveryDate}
            disabled={disabled}
            pickupDate={opportunity.pickupDate}
            onChange={deliveryDate => {
              handleToggleSelected(true)
              handleUpdate({ ...value, deliveryDate })
            }}
          />
        )}

        <div className="input-group input-group--flex mb-0">
          <div className="input-group-addon">
            <Icon icon="hashtag" className="color-dark" />
          </div>
          <TextInput
            className="form-control w-32"
            value={value.quoteNum}
            ref={numRef}
            disabled={disabled}
            maxLength={50}
            name={inputName(`num`)}
            onChange={e => {
              handleToggleSelected(true)
              handleUpdate({ ...value, quoteNum: e.target.value })
            }}
            placeholder="Quote #"
            required={isSelected && settings.requiresQuoteNumber}
          />
        </div>

        <BaseRateField
          model={value}
          onChange={updates => {
            handleToggleSelected(true)
            handleUpdate(updates)
          }}
          required={isSelected}
          disabled={disabled}
          isContractedRate={isContractedRate}
        />

        <SelectInput
          className="w-auto"
          value={value.currency}
          disabled={disabled}
          name={inputName('currency')}
          onChange={e => handleCurrencyChange(e.target.value as Currency)}
          options={currencyOptions}
        />
      </div>

      {value.charges?.length > 1 && (
        <ChargeBreakdownControls
          model={value}
          opportunity={opportunity}
          onChange={v => handleUpdate(v)}
          currency={value.currency}
          exchangeRates={exchangeRates}
        />
      )}

      {isNonUsd &&
        !(value.charges?.length > 1) &&
        currentRate &&
        getAmount(value) > 0 && (
          <div className="mt-1 text-right text-xs text-fv-gray-500">
            &asymp; ${currencyToString(getAmount(value) / currentRate)} USD
            (EXR: {currentRate.toFixed(4)})
          </div>
        )}
    </div>
  )
}

function quoteTypeLabel(quote?: QuoteProps) {
  if (quote?.method === 'api') return 'API'
  if (quote?.method === 'upload') return 'Contract'
  if (quote?.source === 'shipper') return 'Shipper'
  return 'Spot'
}
